import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, DChip, DataTable, EmptyState, Icon, type IconName, type TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { MonthFilterRow } from '../components/MonthFilterRow';
import { Bar } from '../components/Charts';
import {
  currentMonth, daysElapsed, daysOfMonth, lastCompleteMonth, monthChip, monthLabel, monthName,
  monthRange, money, num, ordinalWords, shiftMonth, words,
} from '../lib/scope';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import {
  useAppointments, useAuditLog, useBranches, useDeskClaims, useInvoices, usePatientSources,
} from '../lib/useManagerData';
import { ManagerSurface } from '../components/ManagerSurface';
import { stockTotals, useStockRows } from '../lib/useStockReport';
import { ManagerMenu } from '../components/ManagerMenu';

/**
 * Clinic manager · Reports home — `g_reports.dc.html`.
 *
 * A LIST OF DOORS, FULL WIDTH, NO PANE — the board says so in its own closing
 * line, and it is Law 4 doing the saying: a list of doors has nothing to
 * extend, so there is nothing to dock.
 *
 * THE HEADLINES ARE REAL. Each row prints the month's actual figure, read from
 * the same source the report behind the door reads — one number per row, so the
 * door tells you whether it is worth opening. A headline that cannot be
 * computed says so rather than printing a zero that looks like a fact.
 */
export function ManagerReportsPage() {
  const go = useNavigate();
  const [params] = useSearchParams();
  const [month, setMonth] = useState(params.get('month') || lastCompleteMonth());
  const [compare, setCompare] = useState(shiftMonth(lastCompleteMonth(), -1));
  const [query, setQuery] = useState('');

  const range = useMemo(() => monthRange(month), [month]);
  const prevRange = useMemo(() => monthRange(compare), [compare]);
  const invoices = useInvoices(range);
  const prevInvoices = useInvoices(prevRange);
  const appointments = useAppointments(range);
  const edits = useAuditLog({
    category: 'data_modification', startDate: `${range.start}T00:00:00.000Z`,
    endDate: `${range.end}T23:59:59.999Z`, limit: 100,
  });

  const sources = usePatientSources(range);
  const topSource = sources.channels.find((c) => c.channel !== 'Not said') ?? null;

  usePageMeta({ title: 'Reports' });

  const stock = stockTotals(useStockRows().rows);
  const stockHeadline = [
    `value ${money(stock.totalValue)}`,
    `${stock.expiring} batch${stock.expiring === 1 ? '' : 'es'} expiring`,
    stock.top ? `top-consumed ${stock.top.name}` : null,
  ].filter(Boolean).join(' · ');

  const collected = invoices.page.rows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);
  const prevCollected = prevInvoices.page.rows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);
  const trend = prevCollected
    ? Math.round(((collected - prevCollected) / prevCollected) * 100) : null;

  const visits = appointments.page.rows.filter(
    (a) => a.status !== 'cancelled' && a.status !== 'no-show');
  const followUpShare = visits.length
    ? Math.round((visits.filter((a) => a.appointmentType === 'follow-up').length / visits.length) * 100)
    : 0;

  /* WHAT THE INSURERS OWE is the desk's claims APPROVED and not yet paid, whatever
     month they were filed in — the same figure the accountant's Receivables prints. */
  const desk = useDeskClaims();
  const approvedOpen = desk.rows
    .map((c) => ({ c, out: c.status === 'approved' ? Math.max(0, c.amount - (c.paidAmount ?? 0)) : 0 }))
    .filter((x) => x.out > 0);
  const insuranceOut = approvedOpen.reduce((s, x) => s + x.out, 0);
  const lateClaims = approvedOpen.filter((x) => {
    const at = (x.c.filedAt ?? '').slice(0, 10);
    return at && (Date.now() - new Date(`${at}T00:00:00`).getTime()) / 86_400_000 > 30;
  }).length;
  /* Until the month's reads have answered a figure is not 0, it is not known. */
  const counting = invoices.isLoading || appointments.isLoading || desk.isLoading;

  const discounts = invoices.page.rows.filter((i) => Number(i.discountAmount || 0) > 0).length;
  const voids = invoices.page.rows.filter((i) => i.status === 'cancelled').length;
  /* A REFUND is a bill that was paid more than it now asks — the money went
     back. There is no refund row in the schema, so this is the only honest
     way to count one. */
  const refunds = invoices.page.rows
    .filter((i) => Number(i.amountPaid || 0) > Number(i.total || 0)).length;
  /* Edits to MONEY objects, not every data modification the clinic made —
     the door's headline has to mean what the report behind it counts. */
  const billEdits = edits.page.rows
    .filter((e) => /invoice|bill|payment|claim/i.test(e.resource || '')).length;

  /* NEW PATIENTS — the door's headline is the figure the report behind it
     prints: the same `usePatientSources` query (same key, same cached
     answer), the month's new patients and the channel that brought most. */
  const doors: Array<{
    key: string; icon: IconName; title: string; sub: string; headline: string; to: string;
  }> = [
    {
      key: 'revenue', icon: 'cash', title: 'Revenue & collections',
      sub: 'collected, billed, the rate and the mix',
      headline: counting ? 'counting…' : `${money(collected)}${trend === null ? '' : ` · ${trend >= 0 ? '▲' : '▼'} ${Math.abs(trend)}% vs ${monthChip(compare).split(' ')[0]}`}`,
      to: '/clinic-manager/reports/revenue',
    },
    {
      key: 'visits', icon: 'stethoscope', title: 'Visits & doctors',
      sub: 'volume, return, no-show, flow timings',
      headline: counting ? 'counting…' : `${num(visits.length)} visits · ${followUpShare}% follow-up`,
      to: '/clinic-manager/reports/visits',
    },
    {
      key: 'sources', icon: 'speakerphone', title: 'Patient sources',
      sub: 'which marketing works, in patients',
      headline: `${num(sources.total)} new${topSource ? ` · top: ${topSource.channel.toLowerCase()}` : ''}`,
      to: '/clinic-manager/reports/sources',
    },
    {
      key: 'insurance', icon: 'shield-check', title: 'Insurance & receivables',
      sub: 'the clock, per insurer',
      headline: counting ? 'counting…' : `${money(insuranceOut)} out · ${lateClaims} claim${lateClaims === 1 ? '' : 's'} late`,
      to: '/clinic-manager/reports/insurance',
    },
    {
      key: 'edits', icon: 'flag', title: 'Discounts, refunds & edits',
      sub: 'the audit trail, read as a month',
      headline: counting ? 'counting…' : `${discounts} · ${refunds} · ${billEdits} edits · ${voids} void${voids === 1 ? '' : 's'}`,
      to: '/clinic-manager/reports/edits',
    },
    {
      key: 'stock', icon: 'packages', title: 'Stock & consumption',
      sub: 'value on the shelf, what is being used',
      headline: stockHeadline,
      to: '/clinic-manager/reports/stock',
    },
  ];

  /* ────────────────────────────────────────────────────────────────────────
     THE OPEN MONTH — `g_repzero.dc.html`, "the authored empty month".

     A report is a CLOSED-MONTH instrument. Every figure on every door behind
     this screen is a month's shape: a collection rate, a mix, a return share.
     A month still being lived has none of those yet — it has a running total,
     which is what the ledger is for — so pointing six doors at it would put a
     percent sign on an unfinished number six times over.

     So the current month does not open the doors. It says what it is waiting
     for, shows the one honest figure it does have (what has been collected so
     far, against the last complete month for scale), and offers the two moves
     worth making: go back to the month that closed, or open the ledger, which
     IS the day-by-day instrument.

     Every number below is live; the canvas's sentences are its own.
     ──────────────────────────────────────────────────────────────────────── */
  const openMonth = month === currentMonth();
  const scaleMonth = lastCompleteMonth();
  const scaleInvoices = useInvoices(openMonth ? monthRange(scaleMonth) : null, openMonth);
  const branches = useBranches();

  const elapsed = daysElapsed(month);
  const monthDays = daysOfMonth(month).length;
  const workingDays = new Set(
    invoices.page.rows.filter((i) => Number(i.amountPaid || 0) > 0).map((i) => String(i.date ?? '').slice(0, 10)),
  ).size;
  const scaleCollected = scaleInvoices.page.rows.reduce((s2, i) => s2 + Number(i.amountPaid || 0), 0);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return doors;
    return doors.filter((d) => `${d.title} ${d.sub} ${d.headline}`.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, stockHeadline, collected, visits.length, followUpShare, insuranceOut, lateClaims, counting, discounts, refunds, voids, billEdits, trend, sources.total, topSource?.channel]);

  const narrow = useNarrowViewport();
  const rows: TableRow[] = shown.map((d) => ({
    id: d.key,
    cells: {
      report: (
        <div className="flex" style={{ gap: 10 }}>
          <span className="brand"><Icon name={d.icon} /></span>
          <div>
            <p className="t-strong">{d.title}</p>
            {narrow ? null : <p className="t-cap mut">{d.sub}</p>}
          </div>
        </div>
      ),
      headline: <span className="mut">{d.headline}</span>,
      go: (
        <span className="mut">
          <Icon name="chevron-right" />
        </span>
      ),
    },
  }));

  const doExport = () =>
    openMonth
      ? exportCsv(
        `reports-${month}-so-far`,
        ['Month', 'Collected so far', 'Days in', 'Days that collected'],
        [[month, String(Math.round(collected)), String(elapsed), String(workingDays)]],
      )
      : exportCsv(`reports-${month}`, ['Report', 'Headline'], doors.map((d) => [d.title, d.headline]));

  useShellChrome(
    {
      raw: true,
      /* `g_r1` — a phone's Reports home has no search; the month is the
         compact row under the strip. */
      phoneSearch: false,
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Reports',
      sub: openMonth
        ? `${monthLabel(month)} · all branches · ${words(elapsed)} days in`
        : `${monthLabel(month)} · all branches · vs ${monthName(compare)}`,
      search: 'Search a report, a doctor, a month…',
      searchValue: query,
      onSearchChange: setQuery,
      /* Phone: Export is the door under the list (`g_r1`). */
      ...(narrow ? {} : { primary: { label: 'Export', onClick: doExport } }),
      /* NO PANE. A list of doors has no selection to extend (Law 4). */
    },
    [month, compare, query, collected, visits.length, billEdits, openMonth, elapsed, workingDays, narrow],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <MonthFilterRow
          month={month} onMonth={setMonth}
          compare={compare} onCompare={setCompare}
          branchLine="all branches"
        />
        {openMonth ? (
          <>
            <EmptyState
              title={`${monthName(month)} has ${words(elapsed)} day${elapsed === 1 ? '' : 's'} in it`}
              sub="Reports are a closed-month instrument. The shapes settle after the first full week — until then the daily rhythm is noise and the collection rate has nothing to be a rate of."
              action={{
                label: `Go back to ${monthName(scaleMonth)}`,
                onClick: () => { setMonth(scaleMonth); setCompare(shiftMonth(scaleMonth, -1)); },
              }}
            />
            <div style={{ height: 8 }} />
            <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <div style={{ flex: 1.2, minWidth: 0 }}>
                <Card>
                  <p className="t-ovl mut mb8">What {monthName(month)} does have, today</p>
                  <Bar
                    label={`Collected 1 – ${elapsed} ${monthChip(month).split(' ')[0]}`}
                    value={collected}
                    max={Math.max(collected, scaleCollected)}
                    display={num(collected)}
                  />
                  <Bar
                    label={`${monthName(scaleMonth)}, for scale`}
                    value={scaleCollected}
                    max={Math.max(collected, scaleCollected)}
                    tone="soft"
                    display={num(scaleCollected)}
                  />
                  <p className="t-cap mut">
                    {branches.map((b) => `${b.name} ${num(collected)}`).join(' + ')}
                    {' '}— {words(workingDays)} working day{workingDays === 1 ? '' : 's'} of {words(monthDays)},
                    and the ledger carries them day by day
                  </p>
                  <p className="mt8">
                    <DChip onClick={() => go('/clinic-manager/today/ledger')}>Open the branch ledger</DChip>
                  </p>
                </Card>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Card kind="accent">
                  <p className="t-sec brand">
                    The period control is live on every report, so an empty month is one click from
                    all of them — and it says what it is waiting for instead of drawing a chart of
                    six bars and calling it a month.
                  </p>
                </Card>
              </div>
            </div>
            <p className="t-sec mut" style={{ marginTop: 12 }}>
              Nothing here is broken. This is what a report looks like on the {ordinalWords(elapsed)} of
              the month.
            </p>
          </>
        ) : (
          <>
            <DataTable
              caption="Reports"
              columns={[
                { key: 'report', label: 'Report', width: 46 },
                { key: 'headline', label: `Headline — ${monthLabel(month).split(' ')[0]}`, width: 48 },
                { key: 'go', label: '', width: 6 },
              ]}
              rows={rows}
              onSelect={(r) => {
                const door = doors.find((d) => d.key === r.id);
                if (door?.to) go(door.to);
              }}
              empty={<EmptyState title="No report matches that" sub="Clear the search to see every door again." />}
            />

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <Card kind="accent" style={{ flex: 1, margin: 0, display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}
                onClick={() => go(`/clinic-manager/reports/charts?month=${month}`)}>
                <span className="brand"><Icon name="chart-histogram" /></span>
                <div className="grow">
                  <p className="t-strong">The month in charts</p>
                  <p className="t-cap mut">daily rhythm · mixes</p>
                </div>
                <Icon name="chevron-right" />
              </Card>
              <Card kind="accent" style={{ flex: 1, margin: 0, display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}
                onClick={() => go('/clinic-manager/reports/trends')}>
                <span className="brand"><Icon name="trending-up" /></span>
                <div className="grow">
                  <p className="t-strong">Trends</p>
                  <p className="t-cap mut">months against months</p>
                </div>
                <Icon name="chevron-right" />
              </Card>
            </div>

            {/* `g_r1` — on a phone the export is a door in the list, not only the header button. */}
            {narrow ? (
              <Card kind="accent" style={{ margin: '12px 0 0', display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}
                onClick={doExport}>
                <span className="brand"><Icon name="download" /></span>
                <div className="grow">
                  <p className="t-strong">Export</p>
                  <p className="t-cap mut">any report → CSV</p>
                </div>
                <Icon name="chevron-right" />
              </Card>
            ) : null}

            <p className="t-sec mut" style={{ marginTop: 12 }}>
              Every report exports to CSV, and a list of doors has nothing to extend, so this screen
              has no pane.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
